import api from '@/client/core/api'
import { INSTAGRAM_INSTANTWIN_APPLICANT_SORT_KEYS } from '@/typings/api/instagram_instantwin_applicant/main.schema'

export type ApplicantStatus = 'PROCESSING' | 'WIN' | 'LOSE' | 'MISSING'
export type ApplicantRow = {
  triggerId: number
  applicantAccountId: string | null
  applicantScreenName: string | null
  applicantName: string | null
  applicantPictureUrl: string | null
  scenarioContainerId: number
  nodeId: number
  status: ApplicantStatus
  completedAt: string | null
  startedAt: string | null
  text: string | null
  isNg: boolean
  applicantEntryCount: number
  totalCampaignEntryCount: number
  totalCampaignWinningCount: number
}

export type SortKey = (typeof INSTAGRAM_INSTANTWIN_APPLICANT_SORT_KEYS)[number]
export type SortOrder = 'asc' | 'desc'
export type SortBy = SortKey | string

export type InstagramInstantwinApplicantsData = {
  campaignId: number
  groupId?: number
  prizeId?: number
  applicants: ApplicantRow[]
  total?: number
}

export type InstagramInstantwinApplicantsApiResponse = {
  data: InstagramInstantwinApplicantsData
  total?: number
  error?: {
    type?: string
    message?: string
    status?: number
  }
}

export type GetInstagramInstantwinApplicantsParams = {
  campaignId: number
  sortBy?: SortBy | string
  page?: number
  pageSize?: number
  limitCount?: number
  forCsv?: boolean
  groupId?: number
  prizeId?: number
  keyword?: string
  status?: ApplicantStatus[]
  entryStartAt?: string
  entryEndAt?: string
  applicantCountMin?: number
  applicantCountMax?: number
  totalCampaignEntryCountMin?: number
  totalCampaignEntryCountMax?: number
  totalCampaignWinningCountMin?: number
  totalCampaignWinningCountMax?: number
}

export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 25
export const MAX_PAGE_SIZE = 100
export const DEFAULT_SORT_BY: SortBy = '-entryStartedAt'

const SORT_KEY_SET = new Set<string>(INSTAGRAM_INSTANTWIN_APPLICANT_SORT_KEYS)

const normalizeSortBy = (raw?: SortBy | string): SortBy => {
  if (!raw) {
    return DEFAULT_SORT_BY
  }

  const trimmed = raw.toString().trim()
  const key = trimmed.startsWith('-') ? trimmed.slice(1) : trimmed
  if (!SORT_KEY_SET.has(key)) {
    return DEFAULT_SORT_BY
  }

  return trimmed as SortBy
}

const normalizePageSize = (pageSize?: number): number => {
  const numeric = Number.isFinite(pageSize) ? Number(pageSize) : DEFAULT_PAGE_SIZE
  const positive = Math.max(1, numeric)
  return Math.min(positive, MAX_PAGE_SIZE)
}

export class InstagramInstantwinApplicantsApiError extends Error {
  constructor(
    public type: string,
    message?: string,
    public status?: number
  ) {
    super(message || type)
  }
}

export const buildSortByFromMeta = (sort?: { key?: string; order?: SortOrder }): SortBy => {
  const key = sort?.key && SORT_KEY_SET.has(sort.key) ? (sort.key as SortKey) : undefined
  if (!key) {
    return DEFAULT_SORT_BY
  }
  return sort?.order === 'desc' ? (`-${key}` as SortBy) : (key as SortBy)
}

export async function getInstagramInstantwinApplicants(
  params: GetInstagramInstantwinApplicantsParams
): Promise<InstagramInstantwinApplicantsApiResponse> {
  const sortBy = normalizeSortBy(params.sortBy)
  const forCsv = Boolean(params.forCsv)
  const page = params.page ?? DEFAULT_PAGE
  const pageSize = normalizePageSize(params.pageSize)

  const requestParams: Record<string, unknown> = {
    campaignId: params.campaignId,
    sortBy,
    forCsv
  }

  if (!forCsv) {
    requestParams.page = Math.max(DEFAULT_PAGE, page)
    requestParams.limitCount = pageSize
  }

  if (params.groupId) {
    requestParams.groupId = params.groupId
  }
  if (params.prizeId) {
    requestParams.prizeId = params.prizeId
  }
  if (params.keyword) {
    requestParams.keyword = params.keyword
  }
  if (params.status && params.status.length > 0) {
    requestParams.status = params.status
  }
  if (params.entryStartAt) {
    requestParams.entryStartAt = params.entryStartAt
  }
  if (params.entryEndAt) {
    requestParams.entryEndAt = params.entryEndAt
  }
  if (typeof params.applicantCountMin !== 'undefined') {
    requestParams.applicantCountMin = params.applicantCountMin
  }
  if (typeof params.applicantCountMax !== 'undefined') {
    requestParams.applicantCountMax = params.applicantCountMax
  }
  if (typeof params.totalCampaignEntryCountMin !== 'undefined') {
    requestParams.totalCampaignEntryCountMin = params.totalCampaignEntryCountMin
  }
  if (typeof params.totalCampaignEntryCountMax !== 'undefined') {
    requestParams.totalCampaignEntryCountMax = params.totalCampaignEntryCountMax
  }
  if (typeof params.totalCampaignWinningCountMin !== 'undefined') {
    requestParams.totalCampaignWinningCountMin = params.totalCampaignWinningCountMin
  }
  if (typeof params.totalCampaignWinningCountMax !== 'undefined') {
    requestParams.totalCampaignWinningCountMax = params.totalCampaignWinningCountMax
  }

  const response = await api.get<InstagramInstantwinApplicantsApiResponse>('/instagram_instantwin_applicant', {
    params: requestParams
  })

  if (response?.error?.type) {
    throw new InstagramInstantwinApplicantsApiError(response.error.type, response.error.message, response.error.status)
  }

  return response
}
